<template>
  <Layout
    class="onboarding-notifications"
    :title="page.title"
    :subtitle="page.subtitle"
  >
    <template v-if="image" #aboveTitle>
      <img
        class="onboarding-notifications__image"
        :src="image"
        :width="page.image?.width"
        :height="page.image?.height"
        alt=""
      />
    </template>

    <div class="onboarding-notifications__options">
      <div
        v-for="option in page.options"
        :key="option.title"
        class="onboarding-notifications__option"
      >
        <NotificationsOption :option="option" :view="view" />
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import type { NotificationsPage } from '@/models/onboarding.types'

import Layout from '../Layout.vue'
import NotificationsOption from '../NotificationsOption.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as NotificationsPage
})

const image = computed(() => {
  if (!page.value.image) {
    return
  }

  return getImage(page.value.image.src)
})

onBeforeMount(() => {
  // Set all options as marked by default.
  props.view.setCurrentChoice(page.value.options.map((option) => option.title))
})
</script>

<style lang="scss" scoped>
.onboarding-notifications {
  padding-bottom: 0;

  :deep(.onboarding-layout__above-title) {
    @media only screen and (min-width: $w-tablet-b) {
      order: 3;
    }
  }

  :deep(.onboarding-layout__title) {
    margin-block: 28px;
    @media only screen and (max-height: 754px) {
      margin-block: 16px;
    }
    @media (min-width: $w-tablet-b) {
      margin-top: 0;
    }
  }

  :deep(.onboarding-layout__default-slot) {
    min-height: 0;
    max-width: var(--onboarding-inner-width);
    @media (min-width: $w-tablet-b) {
      margin-bottom: 24px;
    }

    & > * {
      max-width: none;
    }
  }

  :deep(.onboarding-layout__above-title) {
    display: flex;
    justify-content: center;
    min-height: 100px;
  }

  &__image {
    min-height: 0;
    max-height: 100%;
    margin-bottom: var(--onboarding-padding);
  }

  &__options {
    position: relative;
    // The following two lines make this element
    // take 100% of the screen width (so that we can
    // make the lines after each option go from edge
    // to edge).
    left: calc(0px - var(--onboarding-padding));
    width: calc(100% + (var(--onboarding-padding) * 2));
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: calc(var(--onboarding-padding) * 2);
    @media (min-width: $w-tablet-b) {
      padding-bottom: 0;
    }
  }

  &__option {
    padding-inline: var(--onboarding-padding);

    &:not(:last-child) {
      position: relative;
      padding-bottom: 16px;
      margin-bottom: 16px;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background: $c-border-7;
      }
    }
  }

  :deep(.notifications-option) {
    @media (min-width: $w-370) {
      gap: 64px;
    }
  }
}
</style>
